import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useDesigner } from '../../context/DesignerContext.js';
import { saveToJSON, loadFromJSON, exportCSS } from '@bq/designer-core';
import { TokenPanel } from '../../r3/TokenPanel/index.js';
import styles from './DesignerSurface.module.css';

/** BT-01: Maximum file size for JSON load (1 MB). */
const MAX_LOAD_FILE_SIZE = 1_048_576;

export interface DesignerSurfaceProps {
  /** Content rendered inside the A2 Canvas zone. */
  children?: ReactNode;
  className?: string;
}

/**
 * R6 DesignerSurface — top-level layout for the CRD Designer Token Studio.
 *
 * Three semantic A-zones per Chad guardrail C:
 *   A0: Toolbar (actions: undo/redo, save, load, export, reset)
 *   A2: Canvas (observational — live preview of token overrides)
 *   A3: Inspector (mutation authority — TokenPanel in TUNE mode)
 */
export function DesignerSurface({ children, className }: DesignerSurfaceProps) {
  const { document, canUndo, canRedo, undo, redo, reset } = useDesigner();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const overrideCount = document.overrides.size;

  // --- A0 Toolbar actions ---

  const handleSave = useCallback(() => {
    const json = saveToJSON(document);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url;
    a.download = 'token-overrides.json';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000); // BT-05
  }, [document]);

  const handleLoad = useCallback(() => {
    setLoadError(null);
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // BT-01: File size guard
      if (file.size > MAX_LOAD_FILE_SIZE) {
        setLoadError(`File too large (${(file.size / 1024).toFixed(0)} KB). Maximum: 1 MB.`);
        e.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        try {
          const loaded = loadFromJSON(reader.result as string);
          reset(loaded);
          setLoadError(null);
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Unknown error';
          setLoadError(msg);
        }
      };
      reader.readAsText(file);
      // Reset input so the same file can be re-selected
      e.target.value = '';
    },
    [reset],
  );

  const handleExportCSS = useCallback(() => {
    const css = exportCSS(document);
    if (!css) return;
    const blob = new Blob([css], { type: 'text/css' });
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url;
    a.download = 'token-overrides.css';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000); // BT-05
  }, [document]);

  const handleReset = useCallback(() => {
    reset();
    setLoadError(null);
  }, [reset]);

  // --- CSS override style for A2 canvas zone ---
  const canvasOverrideStyle = useMemo(() => {
    const style: Record<string, string> = {};
    for (const [cssVar, override] of document.overrides) {
      style[cssVar] = override.value;
    }
    return style as React.CSSProperties;
  }, [document]);

  return (
    <div
      data-crd-id="r6.bq-designer-surface"
      data-crd-layer="r6"
      className={`${styles.surface} ${className ?? ''}`}
    >
      {/* A0: Toolbar */}
      <div
        data-crd-id="r6.bq-designer-toolbar"
        data-crd-layer="r6"
        className={styles.toolbar}
        role="toolbar"
        aria-label="Designer toolbar"
      >
        <div className={styles.toolbarLeft}>
          <h1 className={styles.toolbarTitle}>Token Studio</h1>
          {overrideCount > 0 && (
            <span className={styles.overrideCount}>
              {overrideCount} {overrideCount === 1 ? 'override' : 'overrides'}
            </span>
          )}
        </div>

        <div className={styles.toolbarCenter}>
          <button
            className={styles.toolbarBtn}
            onClick={undo}
            disabled={!canUndo}
            aria-label="Undo"
          >
            Undo
          </button>
          <button
            className={styles.toolbarBtn}
            onClick={redo}
            disabled={!canRedo}
            aria-label="Redo"
          >
            Redo
          </button>
        </div>

        <div className={styles.toolbarRight}>
          <button
            className={styles.toolbarBtn}
            onClick={handleSave}
            disabled={overrideCount === 0}
            aria-label="Save overrides as JSON"
          >
            Save
          </button>
          <button
            className={styles.toolbarBtn}
            onClick={handleLoad}
            aria-label="Load overrides from JSON"
          >
            Load
          </button>
          <button
            className={styles.toolbarBtn}
            onClick={handleExportCSS}
            disabled={overrideCount === 0}
            aria-label="Export overrides as CSS"
          >
            Export CSS
          </button>
          <button
            className={styles.toolbarBtn}
            onClick={handleReset}
            disabled={overrideCount === 0}
            aria-label="Reset all overrides"
          >
            Reset
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            aria-hidden="true"
          />
        </div>
      </div>

      {/* Load error banner */}
      {loadError && (
        <div className={styles.errorBanner} role="alert">
          <span>Load failed: {loadError}</span>
          <button
            className={styles.errorDismiss}
            onClick={() => setLoadError(null)}
            aria-label="Dismiss error"
          >
            &times;
          </button>
        </div>
      )}

      {/* A2: Canvas (observational) */}
      <div
        data-crd-id="r6.bq-designer-canvas"
        data-crd-layer="r6"
        className={styles.canvas}
        style={canvasOverrideStyle}
        role="region"
        aria-label="Live preview canvas"
      >
        {children}
      </div>

      {/* A3: Inspector (mutation authority) */}
      <div
        data-crd-id="r6.bq-designer-inspector"
        data-crd-layer="r6"
        className={styles.inspector}
        role="complementary"
        aria-label="Token inspector"
      >
        <h2 className={styles.inspectorHeader}>Tokens</h2>
        <TokenPanel />
      </div>
    </div>
  );
}
